import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  pointerWithin,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CheckCircle2, ChevronDown, GripVertical, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/layout/TopBar";
import { usePortfolio, useUpdateProject } from "@/hooks/usePortfolio";
import { DependsOnChips } from "@/components/project/DependsOn";
import { HEALTH_OPTIONS, STATUS_BY_KEY } from "@/lib/projectStatus";
import { fmtMoneyExact } from "@/lib/cost";
import { EBITDA_DEFINITION, annualBenefitTotal, asRealized, costAvoidance, costAvoidanceTotal, inYearBenefit, inYearBenefitTotal, inYearOverride, type BenefitRow, type CostAvoidanceRow } from "@/lib/benefit";
import { readBlendedRate } from "@/lib/cost";
import { useAppSettings } from "@/hooks/useAppSettings";
import { BoardKpiCards, deliveredBoardProjects, isAtRisk } from "@/components/board/BoardKpiCards";
import { TIER_ORDER, TIER_META, TOP10_TIER, byRank, type PriorityTier, type TierMeta } from "@/lib/priorityTier";
import { cn } from "@/lib/cn";
import { shortDate } from "@/lib/format";
import { EnhancementStrip } from "@/components/board/EnhancementStrip";
import { SmallEnhancements } from "@/components/board/SmallEnhancements";
import { RequestDecision, SendToEnhancementsButton } from "@/components/board/RequestDecision";
import { nextUpSections } from "@/lib/nextUpSections";
import { useDepartmentNames } from "@/hooks/useDepartmentNames";
import { NO_DEPARTMENT, ageDays, departmentColor, submittedOn } from "@/lib/departmentColor";
import type { Project } from "@/types/project";

// Top Projects — the business's prioritization board (from the exec
// prioritization deck). Five bands in deck order; the business argues
// about order inside a band, IT promotes across bands as resources
// free up. Tier lives on the project (priority_tier + priority_rank)
// and is orthogonal to lifecycle status — Top 10 rows can still be
// pre-approval, and their status pill says so.
//
// Visual language mirrors /roadmap (Brett, 2026-09-03): tinted
// collapsible band cards with a colored left-edge bar, a column-header
// strip, clickable grid rows. Moving is Jira-backlog style (Brett,
// same day): drag the grip to reorder within a band or drop a row on
// another band to promote/demote — no dropdowns.

// One fixed grid so every band's rows align. Slimmed for the C-level
// read (Brett, 2026-09-04): Department dropped, ID folded under the
// name, Progress merged into the Status cell. Health dot added for the
// exec portfolio review (Brett, 2026-09-09) — sweep the left edge to
// see where the meeting time goes. Columns, left→right:
//   grip · rank · health · Project(+ID) · Sponsor · Team · Status+% ·
//   Go-live · Annual benefit · remove
// Sponsor replaced PM 2026-09-15 (Brett): at C-level/board altitude
// who OWNS the outcome matters more than who runs the plan. Shows
// effective_sponsor so an initiative-inherited sponsor still reads.
//   … · Go-live · Annual benefit · In-year benefit · remove
// In-year (Brett, 2026-09-16): the annual figure prorated by days from
// go-live to Dec 31 — what THIS year actually captures. Unscheduled
// rows say "no go-live date" rather than hiding a $0 inside the total.
// Cost avoidance (Brett, 2026-10-16) sits between Go-live and Annual EBITDA.
function bySubmitted(a: Project, b: Project): number {
  const sa = submittedOn(a) ?? "9999";
  const sb = submittedOn(b) ?? "9999";
  if (sa !== sb) return sa < sb ? -1 : 1;
  return a.id.localeCompare(b.id, undefined, { numeric: true });
}

const BOARD_GRID =
  "20px 24px 14px minmax(120px,1fr) 120px 120px 160px 80px 104px 100px 92px 32px";
// Two-line rows (Brett, 2026-10-16: "the project title can sit above the
// column details so it can display" — names were truncating at 24" and
// worse below). Row 1: the name across every column from Project to the
// end; row 2: the ID + dependency chips under Project, then the details
// under their headers. The grip / rank / health cells span both rows.
const LEAD_CELL = { gridRow: "1 / span 2" } as const;
const NAME_CELL = { gridColumn: "4 / -1" } as const;
const YEAR = new Date().getFullYear();

/** Blended hourly rate (Settings → Cost, default $65) — prices cost avoidance. */
function useBlendedRate(): number {
  const { data } = useAppSettings("cost");
  return readBlendedRate(data);
}

/** Annual-benefit cell. A ONE-TIME figure (a single stocking order —
 *  Brett, 2026-09-16) is not $/yr, so it renders muted with a "one-time"
 *  tag and stays out of the annual totals; the {YEAR} column carries it
 *  in full instead. */
function AnnualCell({ p, empty = "—" }: { p: Project; empty?: string }) {
  if (p.annual_benefit == null) {
    return <span className="text-right text-xs font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">{empty}</span>;
  }
  if (p.benefit_one_time) {
    return (
      <span
        className="flex flex-col items-end leading-tight"
        title={`${fmtMoneyExact(p.annual_benefit)} one-time — not annualized; counted in full in the ${YEAR} column`}
        data-testid="benefit-one-time"
      >
        <span className="text-xs tabular-nums text-ink-muted">
          {fmtMoneyExact(p.annual_benefit)}
        </span>
        <span className="text-[9px] font-semibold uppercase tracking-wide text-ink-subtle">one-time</span>
      </span>
    );
  }
  return (
    <span className="text-right text-xs font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
      {fmtMoneyExact(p.annual_benefit)}
    </span>
  );
}

/** In-year benefit cell — the annual figure prorated by go-live date.
 *  Three states: no figure (—), figure but no date (unscheduled, in
 *  amber so the $0 is a visible gap), or the prorated amount. */
function InYearCell({ row }: { row: BenefitRow }) {
  const annual = row.annual_benefit;
  const goLive = row.end_date;
  const oneTime = !!row.benefit_one_time;
  // An entered figure for this year replaces the proration (NAPA EDI,
  // 2026-09-22) — and needs no go-live date.
  const override = inYearOverride(row, YEAR);
  if (override != null) {
    return (
      <span
        className="text-right text-xs tabular-nums text-emerald-700 dark:text-emerald-300/80"
        data-testid="in-year-cell"
        data-in-year-source="entered"
        title={`${fmtMoneyExact(override)} entered for ${YEAR} on the project page (not prorated)${annual != null ? ` · ${fmtMoneyExact(annual)}${oneTime ? " one-time" : "/yr"}` : ""}`}
      >
        {fmtMoneyExact(override)}
      </span>
    );
  }
  if (annual == null) {
    return <span className="text-right text-xs text-ink-faint">—</span>;
  }
  const v = inYearBenefit(annual, goLive, YEAR, oneTime);
  if (v == null) {
    // Names the missing INPUT, not a status — a completed project with
    // no recorded go-live date lands here too, and "unscheduled" would
    // be wrong for it.
    return (
      <span
        className="text-right text-[10px] font-medium text-amber-600 dark:text-amber-400"
        title={`No go-live date — counts $0 for ${YEAR} until one is set`}
      >
        no go-live date
      </span>
    );
  }
  return (
    <span
      className="text-right text-xs tabular-nums text-emerald-700 dark:text-emerald-300/80"
      data-testid="in-year-cell"
      title={
        oneTime
          ? `${fmtMoneyExact(annual)} one-time, landing ${goLive} — counted in full for ${YEAR}, not prorated`
          : `${fmtMoneyExact(annual)}/yr from ${goLive} through Dec 31, ${YEAR} (prorated by days)`
      }
    >
      {fmtMoneyExact(v)}
    </span>
  );
}

/** Cost-avoidance cell — hours saved per month priced at the blended
 *  rate × 12, or an entered annual figure (2026-10-16). Not EBITDA, so
 *  it never joins the EBITDA totals; it has its own column total. */
function CostCell({ row, rate, empty = "" }: { row: CostAvoidanceRow; rate: number; empty?: string }) {
  const v = costAvoidance(row, rate);
  if (v == null) {
    return <span className="text-right text-xs text-ink-subtle">{empty}</span>;
  }
  const hrs = row.cost_avoidance_hours_per_month;
  const entered = row.cost_avoidance_annual != null;
  return (
    <span
      className="text-right text-xs font-semibold tabular-nums text-sky-700 dark:text-sky-300"
      title={entered ? `${fmtMoneyExact(v)}/yr entered on the project page` : `${hrs} h/mo saved × 12 × ${fmtMoneyExact(rate)}/hr`}
      data-testid="cost-avoidance"
    >
      {fmtMoneyExact(v)}
    </span>
  );
}

/** Two-line column header for a money column: the label, with the
 *  band's sum directly beneath it. A total belongs under the column it
 *  sums (Brett, 2026-09-16) — the old "$X/yr" chip in the band title
 *  sat visually unrelated to the numbers it rolled up. */
function MoneyHeader({
  label,
  title,
  total,
  testId,
  tone = "emerald",
  empty = "—",
}: {
  label: string;
  title: string;
  total: number;
  testId: string;
  tone?: "emerald" | "sky";
  /** What to show while the band has no figure — cost avoidance stays
   *  blank until a sponsor enters one (Brett, 2026-10-16: a column of
   *  dashes "looks broken"). */
  empty?: string;
}) {
  return (
    <div className="text-right" title={title}>
      <div>{label}</div>
      <div
        className={cn("mt-0.5 text-xs font-bold normal-case tracking-normal tabular-nums", tone === "sky" ? "text-sky-700 dark:text-sky-300" : "text-emerald-700 dark:text-emerald-300")}
        data-testid={testId}
      >
        {total > 0 ? fmtMoneyExact(total) : empty}
      </div>
    </div>
  );
}

/** Band-title chip shown ONLY while a band is collapsed — the column
 *  header (and its totals) is hidden then, so the number would
 *  otherwise vanish. Expanded bands show totals in the header instead,
 *  so nothing renders twice. */
function CollapsedTotals({
  annual,
  inYear,
  avoided = 0,
  suffix = "",
}: {
  annual: number;
  inYear: number;
  avoided?: number;
  suffix?: string;
}) {
  if (annual <= 0 && avoided <= 0) return null;
  return (
    <span
      className="shrink-0 text-xs font-semibold tabular-nums text-emerald-700 dark:text-emerald-300"
      title={`Annualized EBITDA${suffix ? " " + suffix : ""} · captured in ${YEAR}${avoided > 0 ? " · cost avoidance" : ""}`}
      data-testid="collapsed-totals"
    >
      {annual > 0 ? `${fmtMoneyExact(annual)}/yr · ${fmtMoneyExact(inYear)} in ${YEAR}` : ""}
      {avoided > 0 && <span className="text-sky-700 dark:text-sky-300">{annual > 0 ? " · " : ""}{fmtMoneyExact(avoided)} avoided</span>}
    </span>
  );
}

// Health dot — solid green/amber/red; hollow gray ring when health was
// never assessed, so an unassessed project can't masquerade as fine.
/** Status pill label — the status word only (Brett, 2026-10-06: "we
 *  should only show the statuses available in the selection"). The
 *  approval day moved to the pill's hover title. */
function statusPillText(_p: Project, shortLabel: string): string {
  return shortLabel;
}

function HealthDot({ health }: { health: string | null | undefined }) {
  const meta = health
    ? HEALTH_OPTIONS.find((h) => h.key === (health ?? "").toLowerCase())
    : undefined;
  if (!meta || meta.key === "n_a") {
    return (
      <span
        className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-inset ring-line-strong"
        title={meta ? "Health: N/A" : "Health not assessed"}
      />
    );
  }
  return (
    <span
      className={cn("inline-block h-2.5 w-2.5 rounded-full", meta.dotClass)}
      title={`Health: ${meta.label}`}
    />
  );
}

/** Droppable id for a band's empty/append area. */
const bandDropId = (key: string) => `band:${key}`;

export function TopProjects() {
  const navigate = useNavigate();
  const { data: projects, isLoading } = usePortfolio();
  const update = useUpdateProject();
  // New / unscoped starts collapsed (2026-09-29): it is the intake
  // funnel now, and can be long — the header count says how much waits.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set(["unscoped"]));
  // View toggle — "By priority" (the tier bands) vs "By team" (the same
  // board grouped by delivery team, answering "how much is on
  // Applications Development / Synnergie / BAs right now"). Persisted
  // in the URL like the exec Gantt's view.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawView = searchParams.get("view");
  // "enhancements" (2026-10-01): the Small Enhancements list beside the
  // project board — one page, two lists, never mixed.
  const view: "priority" | "team" | "enhancements" =
    rawView === "team" ? "team" : rawView === "enhancements" ? "enhancements" : "priority";
  const setView = (v: "priority" | "team" | "enhancements") => {
    const params = new URLSearchParams(searchParams);
    if (v === "priority") params.delete("view");
    else params.set("view", v);
    setSearchParams(params, { replace: true });
  };
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  // Completion is STATUS, not priority — a board project whose status
  // reaches Complete drops out of its band automatically (freeing the
  // slot) and shows in the derived Completed section at the bottom,
  // wearing a chip for the band it came from. No tier change involved:
  // flip the status back and the project returns to its old slot.
  const byTier = useMemo(() => {
    const map = new Map<string, Project[]>();
    for (const p of projects ?? []) {
      if (!p.priority_tier) continue;
      if ((p.status ?? "").toLowerCase() === "complete") continue;
      const arr = map.get(p.priority_tier) ?? [];
      arr.push(p);
      map.set(p.priority_tier, arr);
    }
    for (const [key, arr] of map.entries()) {
      // New / unscoped is an intake queue, not a priority list (Brett,
      // 2026-10-27): oldest request first, so the one that has waited
      // longest for a decision is the first thing on screen.
      if (key === "unscoped") arr.sort(bySubmitted);
      else arr.sort(byRank);
    }
    return map;
  }, [projects]);

  const completedRows = useMemo(() => {
    const tierIdx = new Map(TIER_ORDER.map((t, i) => [t.key, i]));
    return (projects ?? [])
      .filter(
        (p) => p.priority_tier && (p.status ?? "").toLowerCase() === "complete",
      )
      .sort((a, b) => {
        const at = tierIdx.get(a.priority_tier as PriorityTier) ?? 99;
        const bt = tierIdx.get(b.priority_tier as PriorityTier) ?? 99;
        if (at !== bt) return at - bt;
        return byRank(a, b);
      });
  }, [projects]);

  const allBoard = useMemo(
    () => new Map(Array.from(byTier.values()).flat().map((p) => [p.id, p])),
    [byTier],
  );

  // Everything NOT on the board — candidates for the per-band "+ Add"
  // search. Nothing is excluded by status; the picker shows the status
  // pill so a terminal project can still be added knowingly.
  const offBoard = useMemo(
    () => (projects ?? []).filter((p) => !p.priority_tier),
    [projects],
  );

  // Add lands at the bottom of the band; the band un-collapses so the
  // new row is immediately visible.
  const addToBand = (p: Project, tierKey: PriorityTier) => {
    const target = byTier.get(tierKey) ?? [];
    const maxRank = target.reduce(
      (m, t) => Math.max(m, t.priority_rank ?? target.length),
      0,
    );
    update.mutate({ id: p.id, priority_tier: tierKey, priority_rank: maxRank + 1 });
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.delete(tierKey);
      return next;
    });
  };

  const toggleBand = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Remove is reversible: the toast carries an Undo that restores the
  // exact tier + rank. Without it a stray click on the × silently
  // dropped the project with no path back (Brett lost ETE-286 this
  // way, 2026-09-03) — off-board projects appear nowhere on this page.
  const removeFromBoard = (p: Project) => {
    const prevTier = p.priority_tier;
    const prevRank = p.priority_rank;
    update.mutate(
      { id: p.id, priority_tier: null, priority_rank: null },
      {
        onSuccess: () =>
          toast(`${p.id} removed from the board`, {
            description: p.name,
            duration: 10000,
            action: {
              label: "Undo",
              onClick: () =>
                update.mutate({
                  id: p.id,
                  priority_tier: prevTier,
                  priority_rank: prevRank,
                }),
            },
          }),
      },
    );
  };

  // Drop handler — ONE write per drop. The dragged row lands either on
  // another row (insert at that row's slot) or on a band's container
  // (append to that band). New rank = midpoint of the neighbors'
  // effective ranks, so a drop never renumbers the rest of the band.
  const onDragEnd = (e: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const dragged = allBoard.get(String(active.id));
    if (!dragged) return;

    // The intake band keeps date order: a drop inside it is a no-op, a
    // drop into it from another band just changes the band.
    const overIdEarly = String(over.id);
    const overTierEarly = overIdEarly.startsWith("band:") ? overIdEarly.slice(5) : allBoard.get(overIdEarly)?.priority_tier;
    if (overTierEarly === "unscoped") {
      if (dragged.priority_tier !== "unscoped") update.mutate({ id: dragged.id, priority_tier: "unscoped", priority_rank: null });
      return;
    }

    let targetTier: PriorityTier;
    let insertIdx: number;
    const overId = String(over.id);
    if (overId.startsWith("band:")) {
      targetTier = overId.slice(5) as PriorityTier;
      insertIdx = (byTier.get(targetTier) ?? []).length;
    } else {
      const overRow = allBoard.get(overId);
      if (!overRow?.priority_tier) return;
      targetTier = overRow.priority_tier as PriorityTier;
      insertIdx = (byTier.get(targetTier) ?? []).findIndex((p) => p.id === overId);
      if (insertIdx < 0) return;
    }

    // Work against the target band WITHOUT the dragged row, using
    // effective ranks (fall back to display index for unranked rows).
    // Inserting at insertIdx into the without-list reproduces
    // arrayMove semantics for both directions: dragging up places the
    // row before its target, dragging down places it after (the
    // removal already shifted the lower rows up one).
    const band = (byTier.get(targetTier) ?? []);
    const effective = band.map((p, i) => ({ p, rank: p.priority_rank ?? i + 1 }));
    const without = effective.filter((r) => r.p.id !== dragged.id);
    const idx = Math.min(insertIdx, without.length);
    const prev = idx > 0 ? without[idx - 1].rank : null;
    const next = idx < without.length ? without[idx].rank : null;
    let rank: number;
    if (prev != null && next != null) rank = (prev + next) / 2;
    else if (next != null) rank = next - 1;
    else if (prev != null) rank = prev + 1;
    else rank = 1;

    const fields: Record<string, unknown> & { id: string } = {
      id: dragged.id,
      priority_rank: rank,
    };
    if (dragged.priority_tier !== targetTier) fields.priority_tier = targetTier;
    update.mutate(fields);
  };

  const activeDrag = activeDragId ? allBoard.get(activeDragId) : undefined;

  // Scoreboard — shared with /executive (BoardKpiCards) so the two
  // pages can't disagree. The At Risk card is a FILTER: clicking it
  // narrows every band to red/yellow rows.
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  // Cards and totals cover the DECIDED bands only — New / unscoped is
  // the intake funnel, not committed work (2026-09-29).
  const boardRows = useMemo(
    () => Array.from(byTier.entries()).filter(([k]) => k !== "unscoped").flatMap(([, v]) => v),
    [byTier],
  );
  // Completed board projects keep their EBITDA in the totals (Brett,
  // 2026-11-04): the number the board was promised must not shrink
  // because a project delivered.
  const deliveredRows = useMemo(() => deliveredBoardProjects(completedRows), [completedRows]);
  // The EBITDA cards are about the Top 10 only (Brett, 2026-11-04), so
  // they match /executive. Band headers below still total their own band.
  const top10Rows = useMemo(() => byTier.get(TOP10_TIER) ?? [], [byTier]);
  const top10Delivered = useMemo(() => deliveredBoardProjects(completedRows, TOP10_TIER), [completedRows]);

  // Team grouping — same non-complete board projects, keyed by the
  // project's Team field (edited on the project details card). Ordered
  // by load (biggest team first); "No team" sits last as the
  // to-be-assigned queue.
  const byTeam = useMemo(() => {
    const map = new Map<string, Project[]>();
    for (const arr of byTier.values()) {
      for (const p of arr) {
        const team = (p.team ?? "").trim() || "No team";
        const list = map.get(team) ?? [];
        list.push(p);
        map.set(team, list);
      }
    }
    for (const list of map.values())
      list.sort((a, b) => {
        const at = TIER_ORDER.findIndex((t) => t.key === a.priority_tier);
        const bt = TIER_ORDER.findIndex((t) => t.key === b.priority_tier);
        if (at !== bt) return at - bt;
        return byRank(a, b);
      });
    return Array.from(map.entries()).sort((a, b) => {
      if (a[0] === "No team") return 1;
      if (b[0] === "No team") return -1;
      return b[1].length - a[1].length;
    });
  }, [byTier]);

  return (
    <>
      <TopBar
        title="Top Projects"
        subtitle="The business's prioritization, one board — Top 10 first; IT pulls from the lower bands as resources free up. Drag a row to re-rank or move it between bands."
      />
      <div className="space-y-6 p-8">
        {!isLoading && (
          <BoardKpiCards
            rows={boardRows}
            ebitdaRows={top10Rows}
            delivered={top10Delivered}
            scopeLabel="in the Top 10"
            atRiskOnly={atRiskOnly}
            onToggleAtRisk={() => setAtRiskOnly((v) => !v)}
          />
        )}
        {!isLoading && view !== "enhancements" && <EnhancementStrip />}

        {/* Projects | Small Enhancements, then the board's own view toggle. */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-xs" role="group" aria-label="List">
            <button onClick={() => setView("priority")} className={cn("rounded-md px-3 py-1.5 font-medium transition-colors", view !== "enhancements" ? "bg-brand-navy-600 text-white shadow-sm" : "text-ink-3 hover:text-ink")} data-testid="list-projects">
              Projects
            </button>
            <button onClick={() => setView("enhancements")} className={cn("rounded-md px-3 py-1.5 font-medium transition-colors", view === "enhancements" ? "bg-brand-navy-600 text-white shadow-sm" : "text-ink-3 hover:text-ink")} data-testid="list-enhancements">
              Small Enhancements
            </button>
          </div>
          {view !== "enhancements" && (
          <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-xs">
            <button
              onClick={() => setView("priority")}
              className={cn(
                "rounded-md px-3 py-1.5 font-medium transition-colors",
                view === "priority"
                  ? "bg-brand-navy-600 text-white shadow-sm"
                  : "text-ink-3 hover:text-ink",
              )}
            >
              By priority
            </button>
            <button
              onClick={() => setView("team")}
              className={cn(
                "rounded-md px-3 py-1.5 font-medium transition-colors",
                view === "team"
                  ? "bg-brand-navy-600 text-white shadow-sm"
                  : "text-ink-3 hover:text-ink",
              )}
            >
              By team
            </button>
          </div>
          )}
        </div>

        {view === "enhancements" && <SmallEnhancements />}

        {view === "team" && !isLoading && (
          <>
            {byTeam.map(([team, rows]) => (
              <TeamBand
                key={team}
                team={team}
                rows={atRiskOnly ? rows.filter(isAtRisk) : rows}
                collapsed={collapsed.has(`team:${team}`)}
                onToggle={() => toggleBand(`team:${team}`)}
                onNavigate={(pid) => navigate(`/portfolio/${pid}`)}
              />
            ))}
          </>
        )}

        {view !== "priority" ? null : isLoading ? (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Loading…
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={(e) => setActiveDragId(String(e.active.id))}
            onDragEnd={onDragEnd}
            onDragCancel={() => setActiveDragId(null)}
          >
            {TIER_ORDER.map((tier) => (
              <TierBand
                key={tier.key}
                tier={tier}
                rows={byTier.get(tier.key) ?? []}
                delivered={deliveredBoardProjects(deliveredRows, tier.key)}
                collapsed={collapsed.has(tier.key)}
                onToggle={() => toggleBand(tier.key)}
                onNavigate={(pid) => navigate(`/portfolio/${pid}`)}
                onRemove={removeFromBoard}
                candidates={offBoard}
                onAdd={addToBand}
                atRiskOnly={atRiskOnly}
              />
            ))}
            <DragOverlay dropAnimation={{ duration: 180 }}>
              {activeDrag ? (
                <div className="flex items-center gap-2 rounded-lg border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-3 py-2 shadow-lg">
                  <span className="rounded-md bg-brand-navy-100 dark:bg-brand-navy-500/15 px-2 py-0.5 text-[10px] font-bold text-brand-navy-700 dark:text-brand-navy-300">
                    {activeDrag.id}
                  </span>
                  <span className="text-sm font-semibold text-ink">
                    {activeDrag.name}
                  </span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
        {view === "priority" && !isLoading && completedRows.length > 0 && (
          <CompletedBand
            rows={completedRows}
            collapsed={collapsed.has("completed")}
            onToggle={() => toggleBand("completed")}
            onNavigate={(pid) => navigate(`/portfolio/${pid}`)}
            onRemove={removeFromBoard}
          />
        )}
      </div>
    </>
  );
}

// Grid for the By-team view: the rank/grip slots become one tier-chip
// column (priority standing travels with the row); no move controls —
// team is edited on the project details card, priority in the other
// view. Slimmed with the C-level pass: ID under name, no Department,
// Status+% as one cell (and no Team column — the band IS the team).
const TEAM_GRID =
  "88px 14px minmax(120px,1fr) 120px 160px 80px 104px 100px 92px";
const TEAM_LEAD_CELL = { gridRow: "1 / span 2" } as const;
const TEAM_NAME_CELL = { gridColumn: "3 / -1" } as const;

// By-team band — how much board work sits on each delivery team.
function TeamBand({
  team,
  rows,
  collapsed,
  onToggle,
  onNavigate,
}: {
  team: string;
  rows: Project[];
  collapsed: boolean;
  onToggle: () => void;
  onNavigate: (projectId: string) => void;
}) {
  const totalBenefit = annualBenefitTotal(rows);
  const totalInYear = inYearBenefitTotal(rows, YEAR);
  const rate = useBlendedRate();
  const totalAvoided = costAvoidanceTotal(rows, rate);
  const noTeam = team === "No team";
  const top10Count = rows.filter((p) => p.priority_tier === "top10").length;
  return (
    <section
      data-team={team}
      className="overflow-hidden rounded-xl border border-line bg-surface"
      style={{ boxShadow: `inset 6px 0 0 0 ${noTeam ? "#fbbf24" : "#818cf8"}` }}
    >
      <button
        onClick={onToggle}
        className={cn(
          "flex w-full items-center gap-3 pl-6 pr-5 py-4 text-left transition-colors hover:brightness-95",
          noTeam ? "bg-amber-50 dark:bg-amber-500/10" : "bg-brand-navy-50 dark:bg-brand-navy-500/10",
        )}
      >
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-ink-muted transition-transform",
            collapsed && "-rotate-90",
          )}
        />
        <h2 className="shrink-0 text-base font-bold text-ink">{team}</h2>
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums",
            noTeam ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-brand-navy-100 dark:bg-brand-navy-500/15 text-brand-navy-700 dark:text-brand-navy-300",
          )}
        >
          {rows.length}
        </span>
        {top10Count > 0 && (
          <span className="shrink-0 rounded-full bg-brand-navy-600 px-2 py-0.5 text-[10px] font-semibold text-white">
            {top10Count} Top 10
          </span>
        )}
        {noTeam && (
          <span className="truncate text-xs text-ink-muted">
            Assign a team on the project&apos;s details card to move these out.
          </span>
        )}
        {collapsed && (
          <span className="ml-auto">
            <CollapsedTotals annual={totalBenefit} inYear={totalInYear} avoided={totalAvoided} />
          </span>
        )}
      </button>
      {!collapsed && (
        <div className="overflow-x-auto">
          <div className="min-w-[1020px]">
            <div
              className="grid items-center gap-3 border-b border-line-soft bg-canvas/50 px-5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle"
              style={{ gridTemplateColumns: TEAM_GRID }}
            >
              <div>Priority</div>
              <div />
              <div>Project</div>
              <div>Sponsor</div>
              <div>Status</div>
              <div className="text-right">Go-live</div>
              <MoneyHeader
                label="Cost avoidance"
                title={`Hours saved per month × 12 × the blended rate (${fmtMoneyExact(rate)}/hr), or an entered figure — team total beneath`}
                total={totalAvoided}
                testId="band-total-avoided"
                empty=""
                tone="sky"
              />
              <MoneyHeader
                label="Annual EBITDA"
                title="Estimated EBITDA per full year once live — team total beneath"
                total={totalBenefit}
                testId="band-total-annual"
              />
              <MoneyHeader
                label={`${YEAR} EBITDA`}
                title={`Annual EBITDA prorated by days from go-live through Dec 31, ${YEAR} — team total beneath`}
                total={totalInYear}
                testId="band-total-inyear"
              />
            </div>
            <div className="divide-y divide-line-soft">
              {rows.map((p) => {
                const tierMeta = p.priority_tier ? TIER_META[p.priority_tier] : undefined;
                const status = p.status
                  ? STATUS_BY_KEY[p.status as keyof typeof STATUS_BY_KEY]
                  : undefined;
                const pct = Math.round((p.pct_complete ?? 0) * 100);
                return (
                  <div
                    key={p.id}
                    data-row="top-project"
                    onClick={() => onNavigate(p.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onNavigate(p.id);
                    }}
                    className="group grid w-full cursor-pointer items-center gap-x-3 gap-y-0 px-5 py-1.5 text-left transition-colors hover:bg-raised"
                    style={{ gridTemplateColumns: TEAM_GRID }}
                  >
                    {tierMeta ? (
                      <span
                        className={cn(
                          "inline-flex w-fit self-center rounded-full px-2 py-0.5 text-[10px] font-semibold",
                          tierMeta.chipClass,
                        )}
                        style={TEAM_LEAD_CELL}
                      >
                        {tierMeta.key === "top10" && p.priority_rank != null
                          ? `Top 10 · #${Math.round(p.priority_rank)}`
                          : tierMeta.label}
                      </span>
                    ) : (
                      <span style={TEAM_LEAD_CELL} />
                    )}
                    <span className="self-center" style={TEAM_LEAD_CELL}><HealthDot health={p.health} /></span>
                    <span className="min-w-0 text-sm font-semibold leading-tight text-ink" style={TEAM_NAME_CELL}>{p.name}</span>
                    <span className="font-mono text-[10px] text-ink-subtle">{p.id}</span>
                    <span className="truncate text-xs text-ink-3">
        {p.effective_sponsor || p.sponsor || "—"}
      </span>
                    <div className="flex items-center gap-2">
                      {status ? (
                        <span
                          className={cn("inline-flex w-fit shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold", status.pillClass)}
                          title={p.approved_at ? `Approved ${shortDate(p.approved_at)}` : undefined}
                        >
                          {statusPillText(p, status.shortLabel)}
                        </span>
                      ) : (
                        <span className="text-xs text-ink-subtle">—</span>
                      )}
                      {pct > 0 && (
                        <>
                          <div className="h-1.5 w-10 overflow-hidden rounded-full bg-raised">
                            <div
                              className={cn("h-full rounded-full", pct >= 100 ? "bg-emerald-500" : "bg-brand-navy-500")}
                              style={{ width: `${Math.min(100, Math.max(pct, 2))}%` }}
                            />
                          </div>
                          <span className="text-[10px] font-bold tabular-nums text-ink-2">{pct}%</span>
                        </>
                      )}
                    </div>
                    <span className="text-right text-xs tabular-nums text-ink-muted">{shortDate(p.end_date)}</span>
                    <CostCell row={p} rate={rate} />
                    <AnnualCell p={p} />
                    <InYearCell row={p} />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// "+ Add" — per-band search over projects NOT yet on the board. Pick a
// result and it lands at the bottom of this band. This is the missing
// on-ramp: without it the only way onto the board was a database edit.
function AddToBand({
  tier,
  candidates,
  onAdd,
}: {
  tier: TierMeta;
  candidates: Project[];
  onAdd: (p: Project, tierKey: PriorityTier) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    else setQ("");
  }, [open]);

  const TERMINAL = ["complete", "declined", "postponed"];
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return candidates
      .filter(
        (p) =>
          p.name.toLowerCase().includes(s) || p.id.toLowerCase().includes(s),
      )
      .sort((a, b) => {
        // Live work first; complete/declined/postponed matches sink to
        // the bottom (still addable — the pill says what they are).
        const at = TERMINAL.includes((a.status ?? "").toLowerCase()) ? 1 : 0;
        const bt = TERMINAL.includes((b.status ?? "").toLowerCase()) ? 1 : 0;
        if (at !== bt) return at - bt;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 8);
  }, [q, candidates]);

  return (
    <span className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[11px] font-medium text-ink-3 hover:border-brand-navy-300 hover:text-brand-navy-700 dark:hover:text-brand-navy-300"
        title={`Add a project to ${tier.label}`}
      >
        <Plus className="h-3 w-3" />
        Add
      </button>
      {open && (
        <>
          {/* Click-away backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-1 w-96 rounded-lg border border-line bg-surface p-2 shadow-lg">
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
                if (e.key === "Enter" && matches.length === 1) {
                  onAdd(matches[0], tier.key);
                  setOpen(false);
                }
              }}
              placeholder={`Search projects to add to ${tier.label}…`}
              className="w-full rounded-md border border-line px-2 py-1.5 text-sm text-ink outline-none focus:border-brand-navy-400 focus:ring-1 focus:ring-brand-navy-200"
            />
            {q.trim() && (
              <div className="mt-1 max-h-72 overflow-y-auto">
                {matches.length === 0 ? (
                  <div className="px-2 py-3 text-center text-xs text-ink-subtle">
                    No off-board projects match.
                  </div>
                ) : (
                  matches.map((p) => {
                    const status = p.status
                      ? STATUS_BY_KEY[p.status as keyof typeof STATUS_BY_KEY]
                      : undefined;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          onAdd(p, tier.key);
                          setOpen(false);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-raised"
                      >
                        <span className="shrink-0 rounded-md bg-brand-navy-100 dark:bg-brand-navy-500/15 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-brand-navy-700 dark:text-brand-navy-300">
                          {p.id}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm text-ink">
                          {p.name}
                        </span>
                        {status && (
                          <span
                            className={cn(
                              "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold",
                              status.pillClass,
                            )}
                          >
                            {status.shortLabel}
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </>
      )}
    </span>
  );
}

// Completed — a DERIVED band, not a tier: board projects whose status
// reached Complete. They arrive here automatically (and would return
// to their band if the status flipped back), each wearing a chip for
// the band it came from. No drag — done work isn't re-ranked.
function CompletedBand({
  rows,
  collapsed,
  onToggle,
  onNavigate,
  onRemove,
}: {
  rows: Project[];
  collapsed: boolean;
  onToggle: () => void;
  onNavigate: (projectId: string) => void;
  onRemove: (p: Project) => void;
}) {
  const totalBenefit = annualBenefitTotal(rows);
  const rate = useBlendedRate();
  const totalAvoided = costAvoidanceTotal(rows, rate);
  // Realized this year: prorate from the ACTUAL go-live when recorded.
  const totalInYear = inYearBenefitTotal(
    rows.map((p) => ({
      annual_benefit: p.annual_benefit,
      end_date: p.actual_end ?? p.end_date,
      benefit_one_time: p.benefit_one_time,
    })),
    YEAR,
  );
  return (
    <section
      data-tier="completed"
      className="overflow-hidden rounded-xl border border-line bg-surface"
      style={{ boxShadow: "inset 6px 0 0 0 #10b981" }}
    >
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between bg-emerald-50/60 dark:bg-emerald-500/10 pl-6 pr-5 py-4 text-left transition-colors hover:brightness-95"
      >
        <div className="flex min-w-0 items-center gap-3">
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-ink-muted transition-transform",
              collapsed && "-rotate-90",
            )}
          />
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <h2 className="shrink-0 text-base font-bold text-ink">Completed</h2>
          <span className="shrink-0 rounded-full bg-emerald-100 dark:bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
            {rows.length}
          </span>
          <span className="truncate text-xs text-ink-muted">
            Board projects that shipped — they land here automatically when their status reaches Complete.
          </span>
        </div>
        {collapsed && (
          <CollapsedTotals annual={totalBenefit} inYear={totalInYear} avoided={totalAvoided} suffix="realized" />
        )}
      </button>
      {!collapsed && (
        <div className="overflow-x-auto">
          <div className="min-w-[1140px]">
            {/* Same header strip as the tier bands so the realized
                totals sit under the columns they sum. */}
            <div
              className="grid items-center gap-3 border-b border-line-soft bg-canvas/50 px-5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle"
              style={{ gridTemplateColumns: BOARD_GRID }}
            >
              <div />
              <div />
              <div />
              <div>Project</div>
              <div>Sponsor</div>
              <div>Team</div>
              <div>Origin</div>
              <div className="text-right">Went live</div>
              <MoneyHeader
                label="Cost avoidance"
                title={`Hours saved per month × 12 × the blended rate (${fmtMoneyExact(rate)}/hr), or an entered figure — total beneath`}
                total={totalAvoided}
                testId="band-total-avoided"
                empty=""
                tone="sky"
              />
              <MoneyHeader
                label="Annual EBITDA"
                title="Annualized EBITDA now being realized — total beneath"
                total={totalBenefit}
                testId="band-total-annual"
              />
              <MoneyHeader
                label={`${YEAR} EBITDA`}
                title={`Realized in ${YEAR}: prorated by days from the actual go-live — total beneath`}
                total={totalInYear}
                testId="band-total-inyear"
              />
              <div />
            </div>
            <div className="divide-y divide-line-soft">
            {rows.map((p) => {
              const origin = p.priority_tier
                ? TIER_META[p.priority_tier]
                : undefined;
              return (
                <div
                  key={p.id}
                  data-row="top-project"
                  onClick={() => onNavigate(p.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onNavigate(p.id);
                  }}
                  className="group grid w-full cursor-pointer items-center gap-x-3 gap-y-0 px-5 py-1.5 text-left transition-colors hover:bg-raised"
                  style={{ gridTemplateColumns: BOARD_GRID }}
                >
                  <span className="self-center" style={LEAD_CELL}><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /></span>
                  <span style={LEAD_CELL} />
                  <span style={LEAD_CELL} />
                  <span className="min-w-0 text-sm font-semibold leading-tight text-ink-2" style={NAME_CELL}>{p.name}</span>
                  <span className="font-mono text-[10px] text-ink-subtle">{p.id}</span>
                  <span className="truncate text-xs text-ink-3">
        {p.effective_sponsor || p.sponsor || "—"}
      </span>
                  <span className="truncate text-xs text-ink-3">{(p.team ?? "").trim() || "—"}</span>
                  <div className="flex items-center gap-2">
                    {origin ? (
                      <span
                        className={cn(
                          "inline-flex w-fit shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                          origin.chipClass,
                        )}
                        title="The band this project completed from"
                      >
                        {origin.label}
                      </span>
                    ) : (
                      <span />
                    )}
                    <span className="text-[10px] font-bold tabular-nums text-emerald-600 dark:text-emerald-400">100%</span>
                  </div>
                  <span className="text-right text-xs tabular-nums text-ink-muted">
                    {shortDate(p.actual_end ?? p.end_date)}
                  </span>
                  <CostCell row={p} rate={rate} />
                  <AnnualCell p={p} />
                  {/* Completed: benefit runs from the ACTUAL go-live. */}
                  <InYearCell row={{ ...p, end_date: p.actual_end ?? p.end_date }} />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove(p);
                    }}
                    className="rounded p-1 text-ink-ghost opacity-0 transition-opacity hover:bg-rose-50 dark:hover:bg-rose-500/15 hover:text-rose-600 dark:hover:text-rose-400 group-hover:opacity-100"
                    title="Remove from the board"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function TierBand({
  tier,
  rows,
  collapsed,
  onToggle,
  onNavigate,
  onRemove,
  candidates,
  onAdd,
  atRiskOnly = false,
  delivered = [],
}: {
  tier: TierMeta;
  rows: Project[];
  collapsed: boolean;
  onToggle: () => void;
  onNavigate: (projectId: string) => void;
  onRemove: (p: Project) => void;
  candidates: Project[];
  onAdd: (p: Project, tierKey: PriorityTier) => void;
  /** Scoreboard's At Risk filter — bands show only red/yellow rows. */
  atRiskOnly?: boolean;
  /** Completed projects from this band: listed under Completed below,
   *  but their EBITDA stays in this band's totals (Brett, 2026-11-04). */
  delivered?: Project[];
}) {
  // The whole band accepts drops (append) — same highlight the
  // roadmap quarters use. Also keeps EMPTY and COLLAPSED bands as
  // valid drop targets so promotion never requires expanding first.
  const { setNodeRef, isOver } = useDroppable({ id: bandDropId(tier.key) });
  // Band-level benefit rollup — the Top 10's headline number, same
  // slot the roadmap uses for the quarter budget. Completed rows from
  // this band still count: delivering a project never shrinks the total.
  const deliveredBenefit = annualBenefitTotal(delivered);
  const totalBenefit = annualBenefitTotal(rows) + deliveredBenefit;
  const totalInYear = inYearBenefitTotal(rows, YEAR) + inYearBenefitTotal(asRealized(delivered), YEAR);
  const rate = useBlendedRate();
  const totalAvoided = costAvoidanceTotal(rows, rate);

  // Team tallies for the exec glance ("Synnergie 8 · Sys Apps 2") —
  // clickable: filter the band to one team without breaking rank order.
  // Drag is disabled while filtered (a subset reorder would corrupt
  // ranks); clearing the filter restores it.
  const [teamFilter, setTeamFilter] = useState<string | null>(null);
  const teamCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of rows) {
      const t = (p.team ?? "").trim() || "No team";
      m.set(t, (m.get(t) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => {
      if (a[0] === "No team") return 1;
      if (b[0] === "No team") return -1;
      return b[1] - a[1];
    });
  }, [rows]);
  // Department breakdown for the intake band (Brett, 2026-10-27): a
  // stacked bar across the band, one segment per department; click
  // narrows the band to that department, click again clears.
  const [deptFilter, setDeptFilter] = useState<string | null>(null);
  const deptCounts = useMemo(() => {
    if (tier.key !== "unscoped") return [] as Array<[string, number]>;
    const m = new Map<string, number>();
    for (const p of rows) {
      const d = (p.department ?? "").trim() || NO_DEPARTMENT;
      m.set(d, (m.get(d) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => {
      if (a[0] === NO_DEPARTMENT) return 1;
      if (b[0] === NO_DEPARTMENT) return -1;
      return b[1] - a[1] || a[0].localeCompare(b[0]);
    });
  }, [rows, tier.key]);
  const riskRows = atRiskOnly
    ? rows.filter(isAtRisk)
    : rows;
  const teamRows = teamFilter
    ? riskRows.filter((p) => ((p.team ?? "").trim() || "No team") === teamFilter)
    : riskRows;
  const visibleRows = deptFilter
    ? teamRows.filter((p) => ((p.department ?? "").trim() || NO_DEPARTMENT) === deptFilter)
    : teamRows;
  const filtered = teamFilter != null || deptFilter != null || atRiskOnly;
  // Next up groups into Approved / Requested · brief received /
  // Requested · no brief yet (Brett, 2026-10-16). Other bands are one
  // block. Rank numbers run on through the sections.
  const sections = useMemo(
    () => (tier.key === "next_up" ? nextUpSections(visibleRows) : [{ key: "approved" as const, label: "", hint: "", rows: visibleRows }]),
    [tier.key, visibleRows],
  );
  const showSections = tier.key === "next_up" && visibleRows.some((p) => (p.status ?? "").toLowerCase() === "requested");
  const orderedRows = useMemo(() => sections.flatMap((s) => s.rows), [sections]);
  const rowIndex = useMemo(() => new Map(orderedRows.map((p, i) => [p.id, i])), [orderedRows]);

  return (
    <section
      ref={setNodeRef}
      data-tier={tier.key}
      className={cn(
        "overflow-hidden rounded-xl border bg-surface transition-colors",
        isOver ? "border-brand-navy-400 ring-2 ring-brand-navy-200 dark:ring-brand-navy-500/30" : "border-line",
      )}
      style={{
        // 6px tier-colored bar painted as inset box-shadow on the left
        // edge — visible across the header AND rows, immune to the
        // inner divide-y rules (same trick as the roadmap quarters).
        boxShadow: `inset 6px 0 0 0 ${tier.barHex}`,
      }}
    >
      {/* Header row: the collapse toggle is its own button (a nested
          button inside it would be invalid HTML), with the + Add picker
          and the benefit rollup beside it. */}
      <div className={cn("flex w-full items-center gap-3 pl-6 pr-5 py-4", tier.headerBg)}>
        <button
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-ink-muted transition-transform",
              collapsed && "-rotate-90",
            )}
          />
          <h2 className="shrink-0 text-base font-bold text-ink">{tier.label}</h2>
          <span
            className={cn(
              "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums",
              tier.countChip,
            )}
          >
            {rows.length}
          </span>
          <span className="truncate text-xs text-ink-muted">{tier.blurb}</span>
        </button>
        {/* Team tallies — the exec glance: who's carrying this band.
            Click filters the band to that team; click again clears. */}
        {tier.key === "unscoped" && rows.some((p) => p.cfo_agenda) && (
          <span className="rounded-full bg-brand-navy-100 dark:bg-brand-navy-500/30 px-2 py-0.5 text-[10px] font-semibold text-brand-navy-700 dark:text-brand-navy-200" title="Flagged for the CFO approval meeting" data-testid="agenda-count">
            On agenda {rows.filter((p) => p.cfo_agenda).length}
          </span>
        )}
        {teamCounts.length > 0 && (
          <div className="flex shrink-0 items-center gap-1">
            {teamCounts.map(([team, n]) => {
              const active = teamFilter === team;
              const noTeam = team === "No team";
              return (
                <button
                  key={team}
                  type="button"
                  onClick={() => setTeamFilter(active ? null : team)}
                  title={
                    active
                      ? "Clear team filter"
                      : `Show only ${team} rows in this band`
                  }
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums transition-colors",
                    active
                      ? "bg-brand-navy-600 text-white"
                      : noTeam
                        ? "bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 hover:bg-amber-200"
                        : "bg-surface text-ink-3 ring-1 ring-line hover:bg-raised",
                  )}
                >
                  {team} {n}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex shrink-0 items-center gap-3">
          {collapsed && <CollapsedTotals annual={totalBenefit} inYear={totalInYear} avoided={totalAvoided} />}
          <AddToBand tier={tier} candidates={candidates} onAdd={onAdd} />
        </div>
      </div>

      {!collapsed && deptCounts.length > 0 && (
        <DepartmentBar counts={deptCounts} total={rows.length} active={deptFilter} onPick={(d) => setDeptFilter(deptFilter === d ? null : d)} />
      )}
      {!collapsed && (
        rows.length > 0 ? (
          <div className="overflow-x-auto">
            {/* Floor must exceed the grid's fixed columns + gaps +
                px-5 padding, or the minmax(0,1fr) name column
                collapses to zero width. */}
            <div className="min-w-[1140px]">
              <div
                className="grid items-center gap-3 border-b border-line-soft bg-canvas/50 px-5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle"
                style={{ gridTemplateColumns: BOARD_GRID }}
              >
                <div />
                <div className="text-right">#</div>
                <div />
                <div>Project</div>
                <div>Sponsor</div>
                <div>Team</div>
                <div>Status</div>
                {/* The numeric flank (Go-live · Benefit) right-aligns
                    as one block so dates and digits line up. */}
                <div className="text-right">{tier.key === "unscoped" ? "Submitted" : "Go-live"}</div>
                <MoneyHeader
                  label="Cost avoidance"
                  title={`Hours saved per month × 12 × the blended rate (${fmtMoneyExact(rate)}/hr), or an entered figure — band total beneath`}
                  total={totalAvoided}
                  testId="band-total-avoided"
                empty=""
                  tone="sky"
                />
                <MoneyHeader
                  label="Annual EBITDA"
                  title={deliveredBenefit > 0
                    ? `Estimated EBITDA per full year once live — band total beneath, including ${fmtMoneyExact(deliveredBenefit)} from ${delivered.length} completed project${delivered.length === 1 ? "" : "s"} listed under Completed. ${EBITDA_DEFINITION}`
                    : `Estimated EBITDA per full year once live — band total beneath. ${EBITDA_DEFINITION}`}
                  total={totalBenefit}
                  testId="band-total-annual"
                />
                <MoneyHeader
                  label={`${YEAR} EBITDA`}
                  title={`Annual EBITDA prorated by days from go-live through Dec 31, ${YEAR} — band total beneath`}
                  total={totalInYear}
                  testId="band-total-inyear"
                />
                <div />
              </div>
              <SortableContext
                items={orderedRows.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="divide-y divide-line-soft">
                  {sections.map((sec) => (
                    <div key={sec.key} className="divide-y divide-line-soft" data-section={sec.key}>
                      {/* Section strip — only Next up has them (Brett,
                          2026-10-16): approved / brief received / no brief. */}
                      {showSections && (
                        <div
                          className="flex items-center gap-2 bg-canvas/60 px-5 py-1 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle"
                          title={sec.hint}
                          data-testid="band-section"
                        >
                          <span>{sec.label}</span>
                          <span className="rounded-full bg-raised px-1.5 py-px text-[10px] font-bold normal-case tracking-normal text-ink-muted">{sec.rows.length}</span>
                        </div>
                      )}
                      {sec.rows.map((p) => (
                        <BoardRow
                          key={p.id}
                          p={p}
                          idx={rowIndex.get(p.id) ?? 0}
                          tierKey={tier.key}
                          onNavigate={onNavigate}
                          onRemove={onRemove}
                          dragDisabled={filtered}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </SortableContext>
            </div>
          </div>
        ) : (
          <div
            className={cn(
              "px-5 py-6 text-center text-xs italic transition-colors",
              isOver ? "text-brand-navy-600 dark:text-brand-navy-400" : "text-ink-subtle",
            )}
          >
            Nothing in this band — drop a project here.
          </div>
        )
      )}
    </section>
  );
}

function BoardRow({
  p,
  idx,
  tierKey,
  onNavigate,
  onRemove,
  dragDisabled = false,
}: {
  p: Project;
  idx: number;
  tierKey: PriorityTier;
  onNavigate: (projectId: string) => void;
  onRemove: (p: Project) => void;
  /** True while a team filter narrows the band — reordering a subset
   *  would corrupt ranks, so the grip goes inert until the filter clears. */
  dragDisabled?: boolean;
}) {
  const rate = useBlendedRate();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: p.id, disabled: dragDisabled });
  const status = p.status
    ? STATUS_BY_KEY[p.status as keyof typeof STATUS_BY_KEY]
    : undefined;
  const pct = Math.round((p.pct_complete ?? 0) * 100);
  const isRequested = (p.status ?? "").toLowerCase() === "requested";

  return (
    <div
      ref={setNodeRef}
      data-row="top-project"
      onClick={() => onNavigate(p.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onNavigate(p.id);
      }}
      className={cn(
        "group grid w-full cursor-pointer items-center gap-x-3 gap-y-0 px-5 py-1.5 text-left transition-colors hover:bg-raised",
        isDragging && "opacity-30",
      )}
      style={{
        gridTemplateColumns: BOARD_GRID,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      {/* Drag grip — the ONE affordance for re-ranking and moving
          between bands (Jira-backlog style). Hidden while a team
          filter narrows the band. */}
      {dragDisabled ? (
        <span style={LEAD_CELL} />
      ) : (
        <button
          ref={setActivatorNodeRef}
          {...listeners}
          {...attributes}
          onClick={(e) => e.stopPropagation()}
          aria-label="Drag to re-rank or move to another band"
          className="flex h-5 w-4 cursor-grab items-center justify-center self-center text-ink-faint hover:text-ink-3 active:cursor-grabbing"
          style={LEAD_CELL}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      {/* Rank — display position within the band */}
      <span className="self-center text-right text-xs font-bold tabular-nums text-ink-subtle" style={LEAD_CELL}>
        {idx + 1}
      </span>
      {/* Health — the exec-review signal: where the meeting time goes. */}
      <span className="self-center" style={LEAD_CELL}><HealthDot health={p.health} /></span>
      {/* Row 1: the full name, across every column. */}
      <span className="flex min-w-0 items-center gap-3" style={NAME_CELL}>
        <span className="min-w-0 text-sm font-semibold leading-tight text-ink" data-testid="row-name">{p.name}</span>
        {/* Next up (Brett, 2026-10-16): the band is sectioned by decision
            state, so the go / no-go sits quietly at the end of the name
            line instead of across the money columns. */}
        {isRequested && tierKey === "next_up" && (
          <span className="ml-auto shrink-0 pr-1">
            <RequestDecision p={p} />
          </span>
        )}
      </span>
      {/* Row 2 under Project: the ID with dependency chips — "after
          ETE-319", amber until the blocker completes; "+ dep" on hover. */}
      <span className="flex min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap">
        <span className="shrink-0 font-mono text-[10px] text-ink-subtle">{p.id}</span>
        {tierKey === "unscoped" && <DepartmentChip p={p} />}
        <DependsOnChips projectId={p.id} canEdit compact />
      </span>
      {/* PM — who's running it, straight from the project (edit on the
          project page). */}
      <span className="truncate text-xs text-ink-3">
        {p.effective_sponsor || p.sponsor || "—"}
      </span>
      {/* Team — who's building it (edit on the project details card). */}
      <span className={cn("truncate text-xs", p.team ? "text-ink-3" : "text-amber-500")}>
        {(p.team ?? "").trim() || "no team"}
      </span>
      {/* Status + progress — one cell: the pill says the stage, the
          slim bar + % beside it says how far. */}
      <div className="flex items-center gap-2">
        {status ? (
          <span
            className={cn(
              "inline-flex w-fit shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold",
              status.pillClass,
            )}
            data-testid={p.approved_at ? "approved-tag" : undefined}
            title={p.approved_at ? `Approved ${shortDate(p.approved_at)}` : undefined}
          >
            {statusPillText(p, status.shortLabel)}
          </span>
        ) : (
          <span className="text-xs text-ink-subtle">—</span>
        )}
        {pct > 0 && (
          <>
            <div className="h-1.5 w-10 overflow-hidden rounded-full bg-raised">
              <div
                className={cn(
                  "h-full rounded-full",
                  pct >= 100 ? "bg-emerald-500" : "bg-brand-navy-500",
                )}
                style={{ width: `${Math.min(100, Math.max(pct, 2))}%` }}
              />
            </div>
            <span className="text-[10px] font-bold tabular-nums text-ink-2">
              {pct}%
            </span>
          </>
        )}
      </div>
      {/* Go-live — or, in the intake band, the date submitted with its age
          (amber past 30 days: a request nobody has decided on). */}
      {tierKey === "unscoped" ? (
        <SubmittedCell p={p} />
      ) : (
        <span className="text-right text-xs tabular-nums text-ink-muted">{shortDate(p.end_date)}</span>
      )}
      {/* Requested rows carry the decision itself in place of the benefit
          cells (2026-09-29 — the board is the intake funnel); everything
          else shows benefit + the quiet hover-only remove. */}
      {isRequested && tierKey !== "next_up" ? (
        <span className="col-span-4 flex justify-end pr-1">
          <RequestDecision p={p} />
        </span>
      ) : (
      <>
      {/* Cost avoidance — hours saved priced at the blended rate (2026-10-16) */}
      <CostCell row={p} rate={rate} />
      {/* Annual EBITDA */}
      <AnnualCell p={p} empty={tierKey === "top10" ? "TBD" : "—"} />
      {/* In-year benefit — prorated by go-live date (one-time: in full) */}
      <InYearCell row={p} />
      {/* An approved request that turned out to be enhancement-sized (ETE-333) can still go across. */}
      {tierKey === "unscoped" && (p.status ?? "").toLowerCase() === "approved" && <SendToEnhancementsButton p={p} />}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(p);
        }}
        className="rounded p-1 text-ink-ghost opacity-0 transition-opacity hover:bg-rose-50 dark:hover:bg-rose-500/15 hover:text-rose-600 dark:hover:text-rose-400 group-hover:opacity-100"
        title="Remove from the board"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      </>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Intake band extras (Brett, 2026-10-27): department bar, submitted cell,
// inline department chip.
// ---------------------------------------------------------------------------

function DepartmentBar({
  counts,
  total,
  active,
  onPick,
}: {
  counts: Array<[string, number]>;
  total: number;
  active: string | null;
  onPick: (dept: string) => void;
}) {
  return (
    <div className="border-b border-line-soft px-5 pb-3 pt-2" data-testid="dept-bar">
      <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
        <span>By department</span>
        {active && (
          <button type="button" onClick={() => onPick(active)} className="normal-case tracking-normal text-brand-navy-600 hover:underline" data-testid="dept-bar-clear">
            Showing {active} · clear
          </button>
        )}
      </div>
      <div className="flex h-7 w-full overflow-hidden rounded-md" role="group" aria-label="Requests by department">
        {counts.map(([dept, n]) => {
          const c = departmentColor(dept);
          const pct = (n / Math.max(1, total)) * 100;
          const dim = active != null && active !== dept;
          return (
            <button
              key={dept}
              type="button"
              onClick={() => onPick(dept)}
              title={active === dept ? "Clear department filter" : `${dept}: ${n} request${n === 1 ? "" : "s"} — click to show only these`}
              className={cn("flex min-w-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap px-1 text-[11px] font-semibold transition-opacity", dim && "opacity-35")}
              style={{ width: `${pct}%`, backgroundColor: c.bg, color: c.fg, outline: active === dept ? "2px solid #0f172a" : undefined, outlineOffset: -2 }}
              data-testid="dept-segment"
              data-dept={dept}
            >
              <span className="truncate">{dept}</span>
              <span className="tabular-nums opacity-90">{n}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SubmittedCell({ p }: { p: Project }) {
  const on = submittedOn(p);
  const age = ageDays(on);
  const stale = age != null && age > 30;
  return (
    <span className="flex flex-col items-end leading-tight" data-testid="submitted-cell" title={on ? `Submitted ${on}` : "No submitted date"}>
      <span className="text-xs tabular-nums text-ink-muted">{on ? shortDate(on) : "—"}</span>
      {age != null && (
        <span className={cn("text-[10px] tabular-nums", stale ? "font-semibold text-amber-600 dark:text-amber-400" : "text-ink-subtle")}>
          {age === 0 ? "today" : `${age}d`}
        </span>
      )}
    </span>
  );
}

function DepartmentChip({ p }: { p: Project }) {
  const names = useDepartmentNames();
  const update = useUpdateProject();
  const dept = (p.department ?? "").trim();
  const c = departmentColor(dept || null);
  if (dept) {
    return (
      <span
        className="shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold"
        style={{ backgroundColor: c.bg, color: c.fg }}
        title="Department (edit on the project page)"
        data-testid="dept-chip"
      >
        {dept}
      </span>
    );
  }
  // No department yet (most service-desk arrivals): set it right here so
  // the bar stops reading "No department" without opening each project.
  return (
    <select
      value=""
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation();
        const v = e.target.value;
        if (!v) return;
        update.mutate({ id: p.id, department: v }, {
          onSuccess: () => toast.success(`${p.id} → ${v}`),
          onError: (err) => toast.error((err as Error).message || "Couldn't set department"),
        });
      }}
      className="h-5 shrink-0 cursor-pointer rounded-full border border-amber-300 bg-amber-50 px-1.5 text-[10px] font-semibold text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
      title="This request has no department — pick one"
      data-testid="dept-set"
    >
      <option value="">Set department…</option>
      {names.map((n) => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
  );
}
